import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { toast } from "react-toastify";

/**
 * Hook to verify access token on app load
 * Gets accessToken from localStorage and sends it to backend for verification
 * On success, backend sets JWT cookie and frontend redirects to dashboard
 */
export const useVerifyAccessToken = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Skip if verification already attempted
    if (verificationAttempted.current) {
      console.log("[useVerifyAccessToken] Verification already attempted, skipping");
      return;
    }

    const verifyToken = async () => {
      // Check if access token exists in localStorage
      const accessToken = localStorage.getItem("accessToken");
      console.log("[useVerifyAccessToken] Checking for accessToken in localStorage");

      if (!accessToken) {
        console.log("[useVerifyAccessToken] No accessToken found in localStorage");
        verificationAttempted.current = true;
        return;
      }

      console.log("[useVerifyAccessToken] AccessToken found, sending to backend for verification");
      verificationAttempted.current = true;

      try {
        // Send accessToken to backend for verification
        console.log("[useVerifyAccessToken] Making request to /api/auth/verify-access-token");
        const response = await axiosInstance.get("/api/auth/verify-access-token", {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.status === 200 && response.data.success) {
          console.log("[useVerifyAccessToken] Token verification successful!");
          console.log("[useVerifyAccessToken] User data:", response.data.user);
          console.log("[useVerifyAccessToken] JWT cookie has been set by backend");
          
          // Dispatch event with user data so AuthContext can update the user
          const event = new CustomEvent("tokenVerified", {
            detail: { user: response.data.user },
          });
          window.dispatchEvent(event);
          console.log("[useVerifyAccessToken] Dispatched tokenVerified event with user data");
          
          // Keep accessToken in localStorage as a fallback
          // It will be removed on logout or when token expires
          
          // Check if already on a protected route (not login page)
          const isOnAuthPage =
            location.pathname.includes("/log-in") ||
            location.pathname.includes("/sign-up") ||
            location.pathname.includes("/2fa") ||
            location.pathname.includes("/activate");

          if (isOnAuthPage) {
            console.log("[useVerifyAccessToken] On auth page, redirecting to dashboard");
            navigate("/dashboard");
          } else {
            console.log("[useVerifyAccessToken] Already on protected page, staying on current route");
            // Stay on current page - don't redirect
          }
        }
      } catch (error) {
        console.error(
          "[useVerifyAccessToken] Token verification failed:",
          error instanceof Error ? error.message : String(error)
        );

        // Remove invalid token from localStorage on verification failure
        localStorage.removeItem("accessToken");
        console.log("[useVerifyAccessToken] Invalid or expired accessToken removed from localStorage");
      }
    };

    verifyToken();
  }, []); // Empty dependency array - run only once on mount
};
